import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton, IgDivider, IgTypography } from "@intelligems/ig-theme";
import {
  FullAgencyDto,
  OrgShopIdDto,
  StripeApiCustomerDto,
  StripeApiGetAllCustomersActionType,
} from "@intelligems/ig-types";
import { Box, Drawer, Grid } from "@mui/material";
import { useCreateAgencyCustomer } from "@src/api/agency-customers/create-agency-customer";
import { useGetAgencyCustomersByAgencyId } from "@src/api/agency-customers/get-agency-customers-by-agency-id";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useStripeApi } from "@src/api/stripe/stripe-api";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";
import { ChooseCustomerField } from "@src/features/admin/components/Fields/ChooseCustomerField";
import { NoBillingAlert } from "@src/features/admin/components/NoBillingAlert";
import { CreateCustomer } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/CustomerTab/CreateCustomer";
import { closeDrawer, getDrawer } from "@src/store/slices/drawers";
import { theme } from "@src/theme";

interface ContentProps {
  agency: FullAgencyDto;
  orgs: OrgShopIdDto[];
}
const Content = ({ agency, orgs }: ContentProps) => {
  const customers = useGetAgencyCustomersByAgencyId(agency.id);
  const [customer, setCustomer] = useImmer<StripeApiCustomerDto | null>(null);
  const createCustomerGroup = useCreateAgencyCustomer();
  const dispatch = useDispatch();
  if (!customers.isSuccess) {
    return (
      <Box sx={{ borderTop: 1, borderColor: "divider" }}>
        <Grid container direction={"column"} rowSpacing={2} mt={2}>
          <SkeletonGroup lines={4} />
        </Grid>
      </Box>
    );
  }
  return (
    <Box sx={{ borderTop: 1, borderColor: "divider" }}>
      <Grid container direction={"column"} rowSpacing={2} mt={2}>
        {customer?.id && !customer.billing ? (
          <Grid item sx={{ marginTop: "-20px" }}>
            <NoBillingAlert stripeCustomerId={customer.id} />
          </Grid>
        ) : null}
        <Grid item>
          <ChooseCustomerField
            filteredIds={customers.data.customers.map(
              (cg) => cg.stripeCustomerId
            )}
            payload={{
              orgs,
              customer,
            }}
            onChange={(value) => setCustomer(value)}
          />
        </Grid>
        <Grid item textAlign="end">
          <IgButton
            loading={createCustomerGroup.isPending}
            variant={"contained"}
            disabled={!customer}
            onClick={() => {
              if (customer) {
                createCustomerGroup.mutate(
                  {
                    body: {
                      customer: {
                        stripeCustomerId: customer.id,
                        agencyId: agency.id,
                      },
                    },
                    pathParams: { agencyId: agency.id },
                    queryParams: undefined,
                  },
                  {
                    onSuccess: () => {
                      dispatch(closeDrawer("addCustomerGroup"));
                    },
                  }
                );
              }
            }}
          >
            Add
          </IgButton>
        </Grid>
      </Grid>
      <Grid item>
        <IgDivider sx={{ color: theme.palette.text.primary }}>OR</IgDivider>
      </Grid>
      <CreateCustomer
        customer={customer}
        orgs={orgs}
        setCustomer={(customer) => setCustomer(customer)}
      />
    </Box>
  );
};

export const AddCustomerGroupDrawer = () => {
  const org = useGetOrg();
  const drawer = useSelector(getDrawer("addCustomerGroup"));
  const stripeCustomers = useStripeApi<StripeApiGetAllCustomersActionType>({
    action: "getAllCustomers",
    orgs: [{ id: org.data.org.id, shopId: org.data.org.shopId! }],
  });
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeDrawer("addCustomerGroup"));
  };

  if (!drawer?.agency) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open
      onClose={() => handleClose()}
      sx={{
        zIndex: 100,
        ".MuiDrawer-paper": { borderRadius: "0 !important" },
      }}
    >
      <Grid
        direction={"column"}
        container
        sx={{ width: 800, p: 3 }}
        rowSpacing={3}
      >
        <Grid item sx={{ marginBottom: "20px !important" }}>
          <IgTypography variant="h3">
            Add Stripe Customer To Agency
          </IgTypography>
        </Grid>
        {stripeCustomers.isSuccess ? (
          <Content
            agency={drawer.agency}
            orgs={[
              {
                id: org.data.org.id,
                shopId: org.data.org.shopId!,
              },
            ]}
          />
        ) : (
          <SkeletonGroup lines={4} />
        )}
      </Grid>
    </Drawer>
  );
};
